export const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const cleanTime = value => Math.round(value * 1000000) / 1000000;
export const creatorId = () => `c${globalThis.crypto?.randomUUID?.().replace(/-/g, '') || `${Date.now().toString(36)}${Math.random().toString(36).slice(2)}`}`;
export const projectDuration = project => Math.max(0, ...project.tracks.flatMap(track => track.clips.map(clip => clip.start + clip.duration)));
export const findClip = (project, id) => {
  for (const track of project.tracks) {
    const clip = track.clips.find(item => item.id === id);
    if (clip) return { track, clip, asset: project.assets.find(item => item.id === clip.asset_id) };
  }
  return null;
};
export const defaultTransform = () => ({ x: 0.5, y: 0.5, scale: 1, rotation: 0, opacity: 1, fit: 'fit' });
export const fitFades = (clip, duration) => {
  const sum = clip.fade_in + clip.fade_out;
  const ratio = sum > duration ? duration / sum : 1;
  return { fade_in: clip.fade_in * ratio, fade_out: clip.fade_out * ratio };
};
export const createClip = (asset, start = 0) => ({
  id: creatorId(), asset_id: asset?.id || null, start, source_start: 0,
  duration: asset && asset.kind !== 'image' ? asset.duration : 5, speed: 1,
  muted: false, volume_db: 0, pan: 0, fade_in: 0, fade_out: 0,
  transition_in: null, caption: false,
  keyframes: [], animation_offset: 0,
  transform: defaultTransform(), color: { brightness: 0, contrast: 1, saturation: 1 },
  text: asset ? null : { content: 'Nuevo titulo', font: 'sans', size: 64, color: '#ffffff', background: '#00000000', bold: false, align: 'center' },
});
export const createTrack = (type, name, clips = []) => ({
  id: creatorId(), type, name, muted: false, solo: false, hidden: false, locked: false,
  volume_db: 0, opacity: 1, role: 'other', clips,
});
export function migrateCreatorProject(project) {
  if (![1, 2].includes(project?.project_version) || !Array.isArray(project.tracks)) throw new Error('Version de proyecto no compatible.');
  return { ...project, project_version: 2, beat_grid: project.beat_grid || null,
    canvas: { background_mode: 'color', blur_strength: 20, blur_darken: .35, ...project.canvas },
    settings: { snap_to_beat: false, beat_snap_tolerance: .12, template_id: null, export_preset: 'reel', ...project.settings },
    tracks: project.tracks.map(track => ({ role: 'other', ...track, clips: track.clips.map(clip => ({ transition_in: null, caption: false, keyframes: [], animation_offset: 0, ...clip })) })) };
}
export function cleanTimelineReferences(project) {
  const tracks = project.tracks.map(track => {
    const sorted = [...track.clips].sort((a, b) => a.start - b.start);
    return { ...track, clips: track.clips.map(clip => {
      const transition = clip.transition_in;
      if (!transition) return clip;
      const previous = sorted[sorted.findIndex(item => item.id === clip.id) - 1];
      const duration = transition.duration;
      const start = previous && previous.start + previous.duration - (transition.type === 'dissolve' ? duration : 0);
      const valid = previous?.id === transition.from_clip_id && duration >= 0
        && (transition.type === 'cut' ? duration === 0 : duration > 0 && duration <= Math.min(previous.duration, clip.duration) / 2)
        && Math.abs(clip.start - start) <= .00001;
      return valid ? clip : { ...clip, transition_in: null };
    }) };
  });
  const grid = project.beat_grid;
  const source = grid && tracks.flatMap(track => track.clips).find(clip => clip.id === grid.source_clip_id && clip.asset_id === grid.source_asset_id);
  return { ...project, tracks, ...(grid && !source ? { beat_grid: null } : {}) };
}
export function beatTimes(project) {
  const grid = project.beat_grid;
  const source = grid && findClip(project, grid.source_clip_id)?.clip;
  if (!source || source.asset_id !== grid.source_asset_id) return [];
  return grid.timestamps.filter(value => value >= source.source_start && value <= source.source_start + source.duration * source.speed)
    .map(value => cleanTime(source.start + (value - source.source_start) / source.speed));
}
export function addAssetClip(project, asset, start = 0) {
  if (project.tracks.reduce((sum, track) => sum + track.clips.length, 0) >= 80 || start + (asset.kind === 'image' ? 5 : asset.duration) > 1800) return project;
  const empty = project.tracks.find(track => !track.locked && track.type === asset.kind && !track.clips.length);
  if (empty) return { ...project, tracks: project.tracks.map(track => track.id === empty.id ? { ...track, clips: [createClip(asset, start)] } : track) };
  return project.tracks.length >= 16 ? project : { ...project, tracks: [...project.tracks, createTrack(asset.kind, asset.filename.slice(0, 120), [createClip(asset, start)])] };
}
export function addTitle(project, start = 0) {
  return { ...project, tracks: [...project.tracks, createTrack('text', 'Titulo', [createClip(null, start)])] };
}
export function updateClip(project, id, update) {
  const found = findClip(project, id);
  if (!found || found.track.locked) return project;
  const next = typeof update === 'function' ? update(found.clip, found.asset) : { ...found.clip, ...update };
  if (!next || next === found.clip) return project;
  return cleanTimelineReferences({ ...project, tracks: project.tracks.map(track => track.id !== found.track.id ? track : {
    ...track, clips: track.clips.map(clip => clip.id === id ? next : clip),
  }) });
}
export function updateTrack(project, id, update) {
  return { ...project, tracks: project.tracks.map(track => track.id === id ? { ...track, ...update } : track) };
}
export function reorderTrack(project, id, direction) {
  const index = project.tracks.findIndex(track => track.id === id);
  const target = index + direction;
  if (index < 0 || target < 0 || target >= project.tracks.length || project.tracks[index].locked || project.tracks[target].locked) return project;
  const tracks = [...project.tracks];
  [tracks[index], tracks[target]] = [tracks[target], tracks[index]];
  return { ...project, tracks };
}
export function snapTime(time, project, { threshold = 0.12, playhead, exclude = [], snap = true } = {}) {
  if (!snap) return Math.max(0, time);
  const excluded = new Set(exclude);
  const points = [];
  if (project.settings.snap) {
    points.push(0, ...project.markers.map(marker => marker.time));
    if (Number.isFinite(playhead)) points.push(playhead);
    project.tracks.forEach(track => track.clips.forEach(clip => {
      if (!excluded.has(clip.id)) points.push(clip.start, clip.start + clip.duration);
    }));
  }
  const beats = project.settings.snap_to_beat ? beatTimes(project) : [];
  const eligible = [...points.filter(point => Math.abs(point - time) <= threshold),
    ...beats.filter(point => Math.abs(point - time) <= (project.settings.beat_snap_tolerance ?? .12))];
  const nearest = eligible.reduce((best, point) => Math.abs(point - time) < Math.abs(best - time) ? point : best, Infinity);
  return cleanTime(Math.max(0, Number.isFinite(nearest) ? nearest : time));
}
export function moveClips(project, ids, delta, options = {}) {
  const selected = project.tracks.filter(track => !track.locked).flatMap(track => track.clips.filter(clip => ids.includes(clip.id)));
  if (!selected.length || !Number.isFinite(delta)) return project;
  const min = Math.min(...selected.map(clip => clip.start));
  let amount = Math.max(-min, delta);
  const maximum = 1800 - Math.max(...selected.map(clip => clip.start + clip.duration));
  amount = Math.min(maximum, amount);
  if (options.snap !== false) {
    const candidates = selected.flatMap(clip => [clip.start, clip.start + clip.duration]).map(edge => {
      const target = edge + amount;
      return snapTime(target, project, { ...options, exclude: ids }) - target;
    });
    const correction = candidates.reduce((best, candidate) => Math.abs(candidate) > 0.000001 && (best === null || Math.abs(candidate) < Math.abs(best)) ? candidate : best, null);
    if (correction !== null) amount = clamp(amount + correction, -min, maximum);
  }
  if (!amount) return project;
  return cleanTimelineReferences({ ...project, tracks: project.tracks.map(track => track.locked ? track : {
    ...track, clips: track.clips.map(clip => ids.includes(clip.id) ? { ...clip, start: cleanTime(clip.start + amount) } : clip),
  }) });
}
export function trimClip(project, id, edge, time, options = {}) {
  time = snapTime(time, project, { ...options, exclude: [id] });
  return updateClip(project, id, (clip, asset) => {
    const minimum = 1 / project.canvas.fps;
    if (!Number.isFinite(time)) return clip;
    if (edge === 'left') {
      const staticSource = !asset || asset.kind === 'image';
      const start = clamp(time, staticSource ? 0 : Math.max(0, clip.start - clip.source_start / clip.speed), clip.start + clip.duration - minimum);
      const delta = start - clip.start;
      const duration = cleanTime(clip.duration - delta);
      return { ...clip, start: cleanTime(start), source_start: staticSource ? 0 : cleanTime(clip.source_start + delta * clip.speed), duration,
        animation_offset: (clip.animation_offset || 0) + delta * clip.speed,
        ...fitFades(clip, duration) };
    }
    const available = asset && asset.kind !== 'image' ? (asset.duration - clip.source_start) / clip.speed : 3600;
    const duration = cleanTime(clamp(time - clip.start, minimum, Math.min(available, 1800 - clip.start)));
    return { ...clip, duration, ...fitFades(clip, duration) };
  });
}
export function splitClips(project, ids, time, options = {}) {
  time = snapTime(time, project, { ...options, exclude: ids });
  if (project.tracks.flatMap(track => track.clips).length + ids.length > 80) return project;
  let changed = false;
  const tracks = project.tracks.map(track => track.locked ? track : { ...track, clips: track.clips.flatMap(clip => {
    const left = cleanTime(time - clip.start);
    const minimum = 1 / project.canvas.fps;
    if (!ids.includes(clip.id) || left < minimum || clip.duration - left < minimum) return [clip];
    changed = true;
    return [{ ...clip, duration: left, fade_in: Math.min(left, clip.fade_in), fade_out: 0 },
      { ...clip, transition_in: null, id: creatorId(), start: cleanTime(time), source_start: clip.asset_id && project.assets.find(asset => asset.id === clip.asset_id)?.kind !== 'image' ? cleanTime(clip.source_start + left * clip.speed) : 0,
        animation_offset: (clip.animation_offset || 0) + left * clip.speed,
        duration: cleanTime(clip.duration - left), fade_in: 0, fade_out: Math.min(clip.fade_out, clip.duration - left) }];
  }) });
  return changed ? cleanTimelineReferences({ ...project, tracks }) : project;
}
export function deleteClips(project, ids) {
  if (!project.tracks.some(track => !track.locked && track.clips.some(clip => ids.includes(clip.id)))) return project;
  return cleanTimelineReferences({ ...project, tracks: project.tracks.map(track => track.locked ? track : { ...track, clips: track.clips.filter(clip => !ids.includes(clip.id)) }) });
}
export function copyClips(project, ids) {
  return project.tracks.flatMap(track => track.clips.filter(clip => ids.includes(clip.id)).map(clip => ({ track_id: track.id, clip })));
}
export function pasteClips(project, clipboard, time) {
  clipboard = clipboard.filter(item => (!item.clip.asset_id || project.assets.some(asset => asset.id === item.clip.asset_id))
    && project.tracks.some(track => !track.locked && track.id === item.track_id));
  if (!clipboard.length || !Number.isFinite(time) || project.tracks.reduce((sum, track) => sum + track.clips.length, 0) + clipboard.length > 80) return project;
  const origin = Math.min(...clipboard.map(item => item.clip.start));
  if (clipboard.some(item => Math.max(0, time + item.clip.start - origin) + item.clip.duration > 1800)) return project;
  const tracks = project.tracks.map(track => track.locked ? track : { ...track, clips: [...track.clips,
    ...clipboard.filter(item => item.track_id === track.id).map(({ clip }) => ({ ...clip, transition_in: null, id: creatorId(), start: cleanTime(Math.max(0, time + clip.start - origin)) })),
  ] });
  return cleanTimelineReferences({ ...project, tracks });
}
export function duplicateClips(project, ids) {
  const copied = copyClips(project, ids);
  return pasteClips(project, copied, Math.max(0, ...copied.map(item => item.clip.start + item.clip.duration)));
}
export function setClipSpeed(project, id, speed) {
  if (!Number.isFinite(speed)) return project;
  return updateClip(project, id, clip => {
    const next = clamp(speed, 0.25, 2);
    const duration = cleanTime(Math.min(clip.duration * clip.speed / next, 1800 - clip.start));
    return { ...clip, speed: next, duration, ...fitFades(clip, duration) };
  });
}
export function applySyncOffset(project, cameraId, masterId, offsetMs) {
  const camera = findClip(project, cameraId);
  const master = findClip(project, masterId);
  if (!camera || !master || master.track.locked || !Number.isFinite(offsetMs)) return project;
  // Correlation is measured against source zero, independently of existing trims.
  const cameraZero = camera.clip.start - camera.clip.source_start / camera.clip.speed;
  const masterZero = cameraZero + offsetMs / 1000;
  const start = Math.max(0, masterZero + master.clip.source_start / master.clip.speed);
  const source = master.clip.source_start + Math.max(0, -(masterZero + master.clip.source_start / master.clip.speed)) * master.clip.speed;
  const available = (master.asset.duration - source) / master.clip.speed;
  if (Math.min(available, 1800 - start) < 1 / project.canvas.fps) return project;
  return updateClip(project, masterId, clip => {
    const duration = cleanTime(Math.min(clip.duration, available, 1800 - start));
    return { ...clip, start: cleanTime(start), source_start: cleanTime(source), duration, ...fitFades(clip, duration) };
  });
}
export const fadeAt = (clip, time) => {
  const local = time - clip.start;
  if (local < 0 || local >= clip.duration) return 0;
  return Math.min(1, clip.fade_in > 0 ? local / clip.fade_in : 1, clip.fade_out > 0 ? (clip.duration - local) / clip.fade_out : 1);
};
export const trackAudible = (project, track) => !track.muted && (!project.tracks.some(item => item.solo) || track.solo);
export function hasAudibleAudio(project) {
  return project.tracks.some(track => trackAudible(project, track) && track.clips.some(clip => !clip.muted && project.assets.some(asset => asset.id === clip.asset_id && (asset.kind === 'audio' || asset.has_audio))));
}
export const metadataSnapshot = project => JSON.parse(JSON.stringify({ title: project.title, canvas: project.canvas,
  tracks: project.tracks, markers: project.markers, settings: project.settings, beat_grid: project.beat_grid || null }));
export const createHistory = project => ({ past: [], present: metadataSnapshot(project), future: [] });
export function pushHistory(history, project, label = 'Editar') {
  const next = metadataSnapshot(project);
  if (JSON.stringify(next) === JSON.stringify(history.present)) return history;
  return { past: [...history.past, { state: history.present, label }].slice(-80), present: next, future: [] };
}
export function undoHistory(history) {
  if (!history.past.length) return history;
  const previous = history.past[history.past.length - 1];
  return { past: history.past.slice(0, -1), present: previous.state, future: [{ state: history.present, label: previous.label }, ...history.future] };
}
export function redoHistory(history) {
  if (!history.future.length) return history;
  const next = history.future[0];
  return { past: [...history.past, { state: history.present, label: next.label }], present: next.state, future: history.future.slice(1) };
}
export function formatTime(seconds) {
  const value = Math.max(0, seconds || 0);
  return `${String(Math.floor(value / 60)).padStart(2, '0')}:${(value % 60).toFixed(2).padStart(5, '0')}`;
}
